/**
 * PeriodSelector Component
 * Dropdown for selecting the stats time period with optional date range picker
 */

import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { PERIOD_OPTIONS } from './constants';
import DateRangePicker, { formatDateRangeLabel } from './DateRangePicker';

const PeriodSelector = ({ 
  value, 
  onChange,
  customDateRange,
  onCustomDateChange,
  className = '' 
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue === 'custom') {
      setShowDatePicker(true);
    } else {
      onChange(newValue);
    }
  };

  const handleCustomDateApply = (startDate, endDate) => {
    setShowDatePicker(false);
    if (onCustomDateChange) {
      onCustomDateChange(startDate, endDate);
    }
    // Set period to custom with dates encoded
    onChange(`custom_${startDate}_${endDate}`);
  };

  // Get display value for select
  const getDisplayValue = () => {
    if (value && value.startsWith('custom_') && customDateRange?.start && customDateRange?.end) {
      return 'custom';
    }
    return value || 'yesterday';
  };

  // Check if current period is custom
  const isCustomPeriod = value && value.startsWith('custom_');

  return (
    <div className="relative flex items-center gap-1">
      <select
        value={getDisplayValue()}
        onChange={handleChange}
        className={`bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-0 rounded text-xs h-7 px-1 text-gray-900 dark:text-white ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {PERIOD_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.value === 'custom' && isCustomPeriod && customDateRange?.start
              ? `📅 ${formatDateRangeLabel(customDateRange.start, customDateRange.end)}`
              : opt.label}
            {opt.hint && opt.value !== 'custom' ? ` (${opt.hint})` : ''}
          </option>
        ))}
      </select>

      {/* Show calendar icon for quick access when custom is selected */}
      {isCustomPeriod && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowDatePicker(true);
          }}
          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
          title="Αλλαγή ημερομηνιών"
        >
          <Calendar className="w-3 h-3 text-blue-500" />
        </button>
      )}

      {/* Date Range Picker Popup */}
      {showDatePicker && (
        <DateRangePicker
          startDate={customDateRange?.start}
          endDate={customDateRange?.end}
          onApply={handleCustomDateApply}
          onClose={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};

export default PeriodSelector;
